<script setup lang="ts">
import {reactive} from "vue";
import useNotificationSequenceMessageInstances, {
    noCreditMessageInstanceFilters
} from "@/notification-sequences/composables/useNotificationSequenceMessageInstances";
import ConsumerAnimationCell from "@/core/components/animation/ConsumerAnimationCell.vue";
import {Badge} from "@/core/components/ui/badge";
import dateHelper from "@/core/helpers/dateHelper";
import {ExternalLink} from "@lucide/vue";

const params = reactive({
    ...noCreditMessageInstanceFilters(),
    page: 1,
    limit: 10
});

const {data, isValidating} = useNotificationSequenceMessageInstances(params);

const headers = [
    {title: "Patient", key: "consumer", sortable: false, width: "30%"},
    {title: "RCS", key: "template", sortable: false},
    {title: "Séquence", key: "sequence", sortable: false},
    {title: "Annulé le", key: "updated_at", sortable: false},
    {title: "Statut", key: "status", sortable: false}
];

const handlePageChange = (page: number) => {
    params.page = page;
};

const handleItemPerPageUpdate = (limit: number) => {
    params.page = 1;
    params.limit = limit;
};
</script>

<template>
  <v-data-table-server
    :loading="isValidating"
    :headers="headers"
    :items="data?.data"
    item-value="id"
    :items-length="data?.meta?.total ?? 0"
    :items-per-page-options="[10, 20, 30]"
    :items-per-page="params.limit"
    :page="params.page"
    no-data-text="Aucun message non envoyé"
    @update:items-per-page="handleItemPerPageUpdate"
    @update:page="handlePageChange"
  >
    <template #[`item.consumer`]="{item}">
      <ConsumerAnimationCell
        :name="item.consumer?.information?.name ?? 'Anonyme'"
        :birth-date="item.consumer?.information?.birth_date"
        :phone="item.customer?.information?.phone"
        :onboarded="item.customer?.onboarded"
        :editable="false"
      />
    </template>
    <template #[`item.template`]="{item}">
      <Badge variant="secondary" class="whitespace-nowrap">
        {{ item.message?.title }}
      </Badge>
    </template>
    <template #[`item.sequence`]="{item}">
      <router-link
        v-if="item.sequence"
        :to="{
          name: 'patient-administration.communication.sequences.details',
          params: {sequenceId: item.sequence.id}
        }"
        class="text-primary font-medium hover:underline inline-flex items-center gap-1"
      >
        {{ item.sequence.name }}
        <ExternalLink class="size-3.5 shrink-0" />
      </router-link>
    </template>
    <template #[`item.updated_at`]="{item}">
      <span class="text-primary font-medium whitespace-nowrap">
        {{ dateHelper.humanDayTime(item.updated_at) }}
      </span>
    </template>
    <template #[`item.status`]>
      <Badge variant="destructive">
        Annulé
      </Badge>
    </template>
  </v-data-table-server>
</template>
